import React, { ReactElement } from 'react';
import { Grid, Button, TextField, Typography, MenuItem } from '@mui/material';
import Slider from '@mui/material/Slider';
import { TextFieldWithChips } from './TextFieldWithChips';
import { Colors } from '../utils/Colors';
import { Endpoints, useApiRepo } from '../utils/Api';
import { GenericDropdown } from './GenericDropdown';

interface IForm {
  ocdType: string;
  // ocdTypeId: string,
  description: string;
  intensity: number;
  triggers: string[];
  compulsions: string[];
}

const form: any = {
  // ocd_type: '',
  obsession_type_id: '',
  description: '',
  intensity: 0,
  triggers: [],
  compulsions: []
};

export function OcdForm({
  closeForm,
  onFormPost
}: {
  closeForm: any;
  onFormPost?: () => void;
}) {
  const { getObjectsFromAPI } = useApiRepo();
  const [formData, setFormData] = React.useState(form);
  const [error, setError] = React.useState({
    obsession_type_id: false,
    description: false,
    intensity: false,
    triggers: false,
    compulsions: false
  });
  const [ocdTypes, setOcdTypes] = React.useState<string[]>([]);

  async function getOcdTypes() {
    const api = await getObjectsFromAPI();
    const res = await api.get(Endpoints.ocdTypes);
    setOcdTypes(res.data.data);
  }

  React.useEffect(() => {
    getOcdTypes();
  }, []);

  const marks = Array(10)
    .fill(0)
    .map((_, i) => ({ value: i + 1, label: String(i + 1) }));

  async function postForm() {
    const api = await getObjectsFromAPI();
    await api.post(Endpoints.obessions, formData);

    // Allow higher level components to refresh data if desired
    if (onFormPost) {
      onFormPost();
    }
  }

  function generateOcdTypeItems() {
    const menuItems: ReactElement<typeof MenuItem>[] = [];
    ocdTypes.map((type: any) =>
      menuItems.push(
        <MenuItem key={type.id} value={type.id}>
          {type.name}
        </MenuItem>
      )
    );
    return menuItems;
  }

  function validateNotEmpty(value: string | Array<any>) {
    const isError = !value || !value.length;
    return isError;
  }

  function validateField(field: any) {
    const value = formData[field];
    const isValid = validateNotEmpty(value);
    setError((prevState) => ({ ...prevState, [field]: isValid }));
  }
  const handleFormChange = (event: any) => {
    const { value, name } = event.target;
    switch (name) {
      case 'compulsions':
      case 'triggers': {
        setFormData((prevState: any) => ({
          ...prevState,
          [name]: [...prevState[name], value]
        }));
        break;
      }
      default: {
        setFormData((prevState: IForm) => ({ ...prevState, [name]: value }));
      }
    }
  };

  function handleDelete(data: any) {
    const filteredArray = formData[data.type].filter(
      (element: string) => element !== data.value
    );
    setFormData((prevState: IForm) => ({
      ...prevState,
      [data.type]: filteredArray
    }));
  }

  function checkProperties() {
    // TODO apparently I'm doing something terrible here I need to fix
    // eslint-disable-next-line no-restricted-syntax
    for (const key in formData) {
      if (
        (formData[key] === null && formData[key] === '') ||
        formData[key].length === 0
      ) {
        return false;
      }
    }
    return true;
  }

  function submitForm() {
    const isValidForm = checkProperties();
    if (isValidForm) {
      postForm();
      setFormData(form);
    }
  }

  async function submitAndClose() {
    const isValidForm = checkProperties();
    if (isValidForm) {
      postForm();
      closeForm();
    }
  }

  return (
    <Grid container spacing={2} justifyContent="center">
      <Grid item xs={12}>
        <Typography variant="h4" gutterBottom>
          OCD Entry
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <GenericDropdown
          id="obsession_type_id"
          value={formData.obsession_type_id}
          label="Obsession Type"
          onChange={(event) => {
            handleFormChange(event);
          }}
          onBlur={() => validateField('obsession_type_id')}
          items={generateOcdTypeItems()}
          error={error.obsession_type_id}
          helperText={error.obsession_type_id && 'Please select value'}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="description"
          id="description"
          fullWidth
          label="Enter an Obsession"
          variant="outlined"
          value={formData.description}
          onChange={handleFormChange}
          onBlur={() => validateField('description')}
          error={error.description}
          helperText={error.description && "Field can't be empty"}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography align="left" variant="h6" gutterBottom>
          On a scale of 0 to 10, with 10 being the highest, what is the
          intensity level of this obsession
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Slider
          defaultValue={0}
          onChange={handleFormChange}
          aria-label="intensity"
          name="intensity"
          valueLabelDisplay="auto"
          value={formData.intensity}
          min={0}
          max={10}
          step={1}
          marks={marks}
          style={{ color: Colors.skobeloff }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography align="left" variant="h5">
          Triggers
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextFieldWithChips
          name="triggers"
          id="tiggers"
          handleOnChange={handleFormChange}
          label="Enter Triggers"
          chipValues={formData.triggers}
          handleChipDelete={handleDelete}
          onBlur={() => validateField('triggers')}
          error={error.triggers}
          helperText={
            error.triggers && "Field can't be empty. Be Sure to hit 'Enter'"
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Typography align="left" variant="h5">
          Compulsions
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <TextFieldWithChips
          name="compulsions"
          id="compulsions"
          handleOnChange={handleFormChange}
          label="Enter Compulsions"
          chipValues={formData.compulsions}
          handleChipDelete={handleDelete}
          onBlur={() => validateField('compulsions')}
          error={error.compulsions}
          helperText={
            error.compulsions && "Field can't be empty. Be Sure to hit 'Enter'"
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Button
          fullWidth
          disableElevation
          variant="contained"
          style={{
            backgroundColor: Colors.lightGray,
            color: 'black'
          }}
          onClick={submitForm}
        >
          Save & New
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Button
          fullWidth
          disableElevation
          variant="contained"
          style={{
            backgroundColor: Colors.lightGray,
            color: 'black'
          }}
          onClick={submitAndClose}
        >
          Save & Exit
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Button
          fullWidth
          disableElevation
          variant="contained"
          style={{ backgroundColor: Colors.lightGray, color: 'black' }}
          onClick={closeForm}
        >
          Cancel
        </Button>
      </Grid>
    </Grid>
  );
}
